import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Activity, Clock, TrendingDown, Wrench } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reliability() {
  const [data, setData] = useState([]);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reliability/summary?days=${days}`);
      setData(r.data || []);
    } catch (e) {
      toast.error("Erro a carregar métricas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const avgAvail = data.length ? (data.reduce((s, x) => s + x.availability_pct, 0) / data.length).toFixed(1) : 0;
  const totalFail = data.reduce((s, x) => s + x.failures, 0);
  const avgMttr = data.length ? (data.reduce((s, x) => s + x.mttr_minutes, 0) / data.length).toFixed(1) : 0;
  const avgMtbf = data.length ? (data.reduce((s, x) => s + x.mtbf_hours, 0) / data.length).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="reliability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Activity className="h-8 w-8 text-indigo-600" /> Fiabilidade — MTTR / MTBF
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Métricas de manutenção por máquina</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">Período:</span>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32" data-testid="days-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
              <SelectItem value="365">1 ano</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-blue-700 mb-2"><Activity className="h-4 w-4" /><span className="text-xs uppercase">Disponibilidade Média</span></div>
            <div className="text-3xl font-bold text-blue-900">{avgAvail}%</div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700 mb-2"><TrendingDown className="h-4 w-4" /><span className="text-xs uppercase">Avarias no Período</span></div>
            <div className="text-3xl font-bold text-red-900">{totalFail}</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-amber-700 mb-2"><Wrench className="h-4 w-4" /><span className="text-xs uppercase">MTTR Médio</span></div>
            <div className="text-3xl font-bold text-amber-900">{avgMttr} <span className="text-lg">min</span></div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-emerald-700 mb-2"><Clock className="h-4 w-4" /><span className="text-xs uppercase">MTBF Médio</span></div>
            <div className="text-3xl font-bold text-emerald-900">{avgMtbf} <span className="text-lg">h</span></div>
          </CardContent>
        </Card>
      </div>

      {/* Ranking */}
      <Card>
        <CardHeader>
          <CardTitle>Ranking por Máquina (pior primeiro)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-gray-500">A carregar...</p> :
            data.length === 0 ? <p className="text-sm text-gray-500 italic">Sem máquinas.</p> :
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="py-2 px-2">Máquina</th>
                      <th className="py-2 px-2 text-center">Avarias</th>
                      <th className="py-2 px-2 text-center">MTTR (min)</th>
                      <th className="py-2 px-2 text-center">MTBF (h)</th>
                      <th className="py-2 px-2 text-right">Disponibilidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(m => {
                      const availColor = m.availability_pct >= 95 ? "text-emerald-600" : m.availability_pct >= 85 ? "text-amber-600" : "text-red-600";
                      return (
                        <tr key={m.machine_id} className="border-b hover:bg-gray-50" data-testid={`machine-reliability-${m.machine_code}`}>
                          <td className="py-3 px-2 font-medium">{m.machine_name || m.machine_code}</td>
                          <td className="py-3 px-2 text-center">{m.failures}</td>
                          <td className="py-3 px-2 text-center">{m.mttr_minutes}</td>
                          <td className="py-3 px-2 text-center">{m.mtbf_hours}</td>
                          <td className={`py-3 px-2 text-right font-bold ${availColor}`}>{m.availability_pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}
